<template>
  <div class="page-container news-detail-page">
    <div class="page-header">
      <div class="container">
        <h1>{{ $t('pages.news_detail_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ $t('menu.home') }}</router-link> /
          <router-link to="/news">{{ $t('pages.news_list_title') }}</router-link> /
          <span>{{ $t('pages.news_detail_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：新闻详情 -->
        <el-col :span="24" :xs="24">
          <div class="detail-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="10" animated />
            </div>
            <div v-else-if="!newsDetail" class="empty-data">
              <el-empty :description="$t('common.no_data')" />
            </div>
            <div v-else class="news-article">
              <h2 class="article-title">{{ newsDetail.title }}</h2>
              <div class="article-meta">
                <span>{{ t('pages.publish_time') }}: {{ newsDetail.publish_time.split('T')[0] }}</span>
                <span>{{ $t('pages.source') }}: {{ newsDetail.source.length ? newsDetail.source : $t('common.pizu') }}</span>
                <span>{{ $t('pages.author') }}: {{ newsDetail.author.length ? newsDetail.author : $t('common.pizu') }}</span>
                <span>{{ $t('pages.hit_count') }}: {{ newsDetail.hit_count }}</span>
              </div>
              <div class="article-summary" v-if="newsDetail.summary.length">
                <strong>{{ $t('pages.news_detail_summary') }}:</strong> {{ newsDetail.summary }}
              </div>
              <!-- 直接将content绑定到v-html，移除多余嵌套 -->
              <div class="article-content" v-html="newsDetail.content"></div>
<!--              <div class="back-link">-->
<!--                <el-button type="primary" plain @click="$router.push('/news')">-->
<!--                  {{ $t('pages.back_to_list') }}-->
<!--                </el-button>-->
<!--              </div>-->
            </div>
          </div>
        </el-col>

<!--        &lt;!&ndash; 右侧：侧边栏（相关新闻） &ndash;&gt;-->
<!--        <el-col :span="6" :xs="24" class="sidebar-col">-->
<!--          <div class="sidebar">-->
<!--            <div class="sidebar-block">-->
<!--              <h4>{{ $t('pages.related_news') }}</h4>-->
<!--              <ul>-->
<!--                <li v-for="related in relatedNews" :key="related.id">-->
<!--                  <router-link :to="`/news/${related.id}`">{{ related.title }}</router-link>-->
<!--                </li>-->
<!--              </ul>-->
<!--            </div>-->
<!--          </div>-->
<!--        </el-col>-->
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ElMessage } from 'element-plus';
import { getNewsDetailApi } from '@/assets/api/website'; // 假设的接口文件

// 定义新闻详情的类型接口，规范数据类型
interface NewsDetail {
  id: number;
  title: string;
  summary: string;
  content: string;
  cover_image: string;
  publish_time: string;
  source: string;
  author: string;
  hit_count: number;
  category: {
    id: number;
    name: string;
  };
}

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();

const loading = ref(true);
const newsDetail = ref<NewsDetail | null>(null);

// 模拟相关新闻（根据语言动态显示）
const relatedNews = computed(() => [
  { id: 101, title: `${t('pages.related_news_1')} (${locale.value})` },
  { id: 102, title: `${t('pages.related_news_2')} (${locale.value})` },
  { id: 103, title: `${t('pages.related_news_3')} (${locale.value})` },
]);

/**
 * 获取新闻详情数据
 * @param id 新闻ID
 */
const fetchNewsDetail = async (id: number) => {
  if (!id) return;

  loading.value = true;
  try {
    // 修正接口调用参数格式（request.get的第二个参数是config，参数应该放在params里）
    const res = await getNewsDetailApi(id, {
      lang: locale.value,
    });

    if (res) {
      newsDetail.value = res;
    } else {
      ElMessage.warning(t('common.no_data'));
    }
  } catch (error) {
    console.error("获取新闻详情失败:", error);
    ElMessage.error(t('common.request_error'));
  } finally {
    loading.value = false;
  }
};

// 监听路由参数变化（比如从其他新闻详情页跳转过来）
watch(
  () => route.params.id,
  (newId) => {
    const newsId = parseInt(newId as string);
    if (newsId) {
      fetchNewsDetail(newsId);
    } else {
      loading.value = false;
      ElMessage.warning(t('pages.invalid_news_id'));
      router.push('/news');
    }
  },
  { immediate: true }
);
watch(() => locale.value, () => {
  if (route.params.id) {
    const newsId = parseInt(route.params.id as string);
    if (!isNaN(newsId)) {
      fetchNewsDetail(newsId);
    }
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/css/common.scss';

.news-detail-page {
    .page-header {
      padding: 100px 0 40px;
      /* 背景优化：渐变 + 背景图叠加 */
      background:
        linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%),
        url('@/assets/img/header.jpg') center center / cover no-repeat;
      /* 高斯模糊：使用伪元素实现（避免模糊影响内容），模糊值 2px（极轻微） */
      position: relative;
      border-bottom: 1px solid rgba($primary-color, 0.1);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: inherit;
        filter: blur(2px);
        z-index: -1;
      }

      h1 {
        font-family: $font-serif;
        font-size: 2.5rem;
        font-weight: 700;
        color: $primary-color-2;
        margin-bottom: 20px;
        letter-spacing: 5px;
        /* 浅白色文字阴影：极淡、低模糊、小偏移，仅增强文字层次感 */
        text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
        position: relative; // 确保文字在模糊层上方
      }

      .breadcrumb {
        font-size: 14px;
        color: #999;
        position: relative; // 确保面包屑在模糊层上方

        a {
          color: #666;
          text-decoration: none;

          &:hover {
            color: $primary-color;
          }
        }

        span {
          color: $primary-color;
        }
      }
    }
  }

  .page-content {
    padding: 50px 10px;
  }

  .detail-wrapper {
    min-height: 600px;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }

  .news-article {
    .article-title {
      font-size: 28px;
      color: #333;
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      letter-spacing: 1px;
      border-bottom: 1px solid #eee;
    }

    .article-meta {
      display: flex;
      justify-content: center;
      gap: 20px;
      font-size: 14px;
      color: #999;
      margin-bottom: 30px;
    }

    .article-summary {
      background-color: #f9f9f9;
      padding: 15px;
      border-left: 4px solid $primary-color;
      margin-bottom: 30px;
      line-height: 1.8;
      color: #666;
    }

    // 优化content样式，确保v-html渲染的内容样式正常
    .article-content {
      line-height: 1.8;
      color: #333;
      font-size: 16px;
      padding: 0 10px;

      // 深度选择器，确保v-html内的元素样式生效
      :deep(*) {
        box-sizing: border-box;
      }

      :deep(p) {
        margin-bottom: 15px;
        text-align: justify;
        text-indent: 2em;
      }
      :deep(figure.image_resized) {
        margin: 0 auto;
      }
      :deep(img) {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 20px auto;
        border-radius: 6px;
        object-fit: cover;
      }

      :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
        margin: 20px 0 15px;
        color: #222;
      }

      :deep(ul), :deep(ol) {
        margin-bottom: 15px;
        padding-left: 20px;
      }

      :deep(li) {
        margin-bottom: 8px;
      }

      :deep(a) {
        color: $primary-color;
        text-decoration: underline;

        &:hover {
          color: darken($primary-color, 10%);
        }
      }

      :deep(blockquote) {
        border-left: 4px solid #ddd;
        padding: 10px 15px;
        background-color: #f9f9f9;
        margin-bottom: 15px;
      }
    }

    .back-link {
      margin-top: 40px;
      text-align: center;
    }
  }

  .sidebar {
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .sidebar-block {
      h4 {
        font-size: 18px;
        color: $primary-color;
        border-bottom: 2px solid $primary-color;
        padding-bottom: 10px;
        margin-bottom: 15px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          margin-bottom: 10px;
          font-size: 14px;

          a {
            color: #666;
            text-decoration: none;
            transition: color 0.3s;
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &:hover {
              color: $primary-color;
            }
          }
        }
      }
    }
  }

  // 响应式调整
  @media (max-width: 768px) {
    .page-header {
      padding: 100px 0 20px;
    }

    .article-meta {
      flex-direction: column;
      align-items: center;
      gap: 5px;
    }

    .article-title {
      font-size: 24px !important;
    }

    .sidebar-col {
      margin-top: 30px;
    }

    .detail-wrapper {
      min-height: 400px;
      padding: 15px;
    }
}
</style>